import { NotFoundPage } from "@/common/components/NotFoundPage";
import { ProtectedRoute } from "@/common/components/ProtectedRoute";
import { PublicOnlyRoute } from "@/common/components/PublicOnlyRoute";
import { AppLayout } from "@/common/components/layout/AppLayout";
import { APPRECIATION_ENABLED } from "@/common/lib/appreciationEnabled";
import { AccountProvider, RequireRole } from "@/features/accounts/hooks/useCurrentAccount";
import { AcceptInvitationPage } from "@/features/accounts/pages/AcceptInvitationPage";
import { AjustesPage } from "@/features/accounts/pages/AjustesPage";
import { CreateAccountPage } from "@/features/accounts/pages/CreateAccountPage";
import { MembersPage } from "@/features/accounts/pages/MembersPage";
import { AdminLayout } from "@/features/admin-auth/components/AdminLayout";
import { AdminProtectedRoute } from "@/features/admin-auth/components/AdminProtectedRoute";
import { AdminPublicOnlyRoute } from "@/features/admin-auth/components/AdminPublicOnlyRoute";
import { AdminAccessRequestDetailPage } from "@/features/admin-auth/pages/AdminAccessRequestDetailPage";
import { AdminAccessRequestsPage } from "@/features/admin-auth/pages/AdminAccessRequestsPage";
import { AdminLoginPage } from "@/features/admin-auth/pages/AdminLoginPage";
import { AnimalDetailPage } from "@/features/animals/pages/AnimalDetailPage";
import { AnimalTimelinePage } from "@/features/animals/pages/AnimalTimelinePage";
import { HatoListPage } from "@/features/animals/pages/HatoListPage";
import { ImportHatoPage } from "@/features/animals/pages/ImportHatoPage";
import { RegisterAnimalPage } from "@/features/animals/pages/RegisterAnimalPage";
import { AccessRequestPage } from "@/features/auth/pages/AccessRequestPage";
import { AuthCallbackPage } from "@/features/auth/pages/AuthCallbackPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { CostAllocationDetailPage } from "@/features/cost-allocations/pages/CostAllocationDetailPage";
import { CostAllocationsListPage } from "@/features/cost-allocations/pages/CostAllocationsListPage";
import { DepreciacionPage } from "@/features/depreciation/pages/DepreciacionPage";
import { OnboardingCuentasPage } from "@/features/journal/pages/OnboardingCuentasPage";
import { PlanDeCuentasPage } from "@/features/journal/pages/PlanDeCuentasPage";
import { ReferenceValuesPage } from "@/features/journal/pages/ReferenceValuesPage";
import { ReportesPage } from "@/features/journal/pages/ReportesPage";
import { LotsListPage } from "@/features/lots/pages/LotsListPage";
import { RecibidoDetailPage } from "@/features/received-invoices/pages/RecibidoDetailPage";
import { RecibidosListPage } from "@/features/received-invoices/pages/RecibidosListPage";
import { PingPage } from "@/pages/app/PingPage";
import { LandingPage } from "@/pages/landing/LandingPage";
import { PrivacyPolicyPage } from "@/pages/legal/PrivacyPolicyPage";
import { TermsAndConditionsPage } from "@/pages/legal/TermsAndConditionsPage";
import { Navigate, createBrowserRouter, redirect } from "react-router-dom";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/login",
    element: (
      <PublicOnlyRoute>
        <LoginPage />
      </PublicOnlyRoute>
    ),
  },
  {
    path: "/access-request",
    element: (
      <PublicOnlyRoute>
        <AccessRequestPage />
      </PublicOnlyRoute>
    ),
  },
  {
    path: "/legal/privacy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/legal/terms",
    element: <TermsAndConditionsPage />,
  },
  {
    // Intencionalmente SIN PublicOnlyRoute: el callback debe poder correr con
    // un user ya autenticado (ej. segunda pestaña) para completar el bootstrap.
    path: "/auth/callback",
    element: <AuthCallbackPage />,
  },
  {
    // Pública: la página maneja internamente el check de auth
    path: "/invitations/accept",
    element: <AcceptInvitationPage />,
  },
  {
    path: "/onboarding/create-account",
    element: (
      <ProtectedRoute>
        <CreateAccountPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <AccountProvider>
          <AppLayout />
        </AccountProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        loader: () => redirect("/app/hato"),
      },
      {
        path: "hato",
        element: <HatoListPage />,
      },
      {
        path: "hato/import",
        element: <ImportHatoPage />,
      },
      {
        path: "hato/nuevo",
        element: <RegisterAnimalPage />,
      },
      {
        path: "hato/:animalId",
        element: <AnimalDetailPage />,
      },
      {
        path: "hato/:animalId/timeline",
        element: <AnimalTimelinePage />,
      },
      {
        path: "lotes",
        element: <LotsListPage />,
      },
      {
        path: "costos",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <CostAllocationsListPage />
          </RequireRole>
        ),
      },
      {
        path: "costos/:costId",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <CostAllocationDetailPage />
          </RequireRole>
        ),
      },
      {
        // operator queda fuera (mismo criterio que Costos): el backend le
        // responde 404 anti-enumeración en todos los endpoints del feature.
        path: "recibidos",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <RecibidosListPage />
          </RequireRole>
        ),
      },
      {
        path: "recibidos/:invoiceId",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <RecibidoDetailPage />
          </RequireRole>
        ),
      },
      {
        path: "depreciacion",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <DepreciacionPage />
          </RequireRole>
        ),
      },
      {
        path: "reportes",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <ReportesPage />
          </RequireRole>
        ),
      },
      {
        // B43: alias legacy. La ruta canónica nueva es /app/ajustes/mapeo-cuentas.
        // Mantenemos este redirect para no romper bookmarks ni links del banner
        // "faltan mappings" cacheado en clientes con sesión vieja.
        path: "onboarding/cuentas",
        element: <Navigate to="/app/ajustes/mapeo-cuentas" replace />,
      },
      {
        path: "ajustes",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <AjustesPage />
          </RequireRole>
        ),
      },
      {
        path: "ajustes/mapeo-cuentas",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <OnboardingCuentasPage />
          </RequireRole>
        ),
      },
      {
        // Sprint 1 (2026-05-16): nueva página para administrar el árbol
        // contable. owner/admin pueden crear/renombrar/borrar; viewer solo
        // lee.
        path: "ajustes/plan-de-cuentas",
        element: (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <PlanDeCuentasPage />
          </RequireRole>
        ),
      },
      {
        // Sprint 3 (2026-05-17): tablas referenciales A + B usadas por el
        // cron de apreciación. owner/admin editan; viewer solo lee; operator
        // → 404 (no toca contabilidad).
        //
        // Apreciación apagada en v1 (apreciacion-apagada-v1.md): la página no
        // se muestra ni navegando directo por URL — redirige a Ajustes.
        // Revertir = flipar APPRECIATION_ENABLED en
        // common/lib/appreciationEnabled.ts (vuelve el <RequireRole> de abajo).
        path: "ajustes/valores-referenciales",
        element: APPRECIATION_ENABLED ? (
          <RequireRole roles={["owner", "admin", "viewer"]}>
            <ReferenceValuesPage />
          </RequireRole>
        ) : (
          <Navigate to="/app/ajustes" replace />
        ),
      },
      {
        path: "members",
        element: (
          <RequireRole roles={["owner", "admin"]}>
            <MembersPage />
          </RequireRole>
        ),
      },
      {
        path: "ping",
        element: <PingPage />,
      },
    ],
  },
  {
    path: "/admin/login",
    element: (
      <AdminPublicOnlyRoute>
        <AdminLoginPage />
      </AdminPublicOnlyRoute>
    ),
  },
  {
    path: "/admin",
    element: (
      <AdminProtectedRoute>
        <AdminLayout />
      </AdminProtectedRoute>
    ),
    children: [
      {
        index: true,
        loader: () => redirect("/admin/access-requests"),
      },
      {
        path: "access-requests",
        element: <AdminAccessRequestsPage />,
      },
      {
        path: "access-requests/:id",
        element: <AdminAccessRequestDetailPage />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
